import { FC, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useWalletContext } from '../context/WalletContext';
import { router } from '../router';
import { APP_VERSION } from '../config/version';
import { toast } from 'react-hot-toast';
import { BifrostService } from '../services/BifrostService';
import { SourcfulEnergyLogo } from '../components/icons/brand_icons';
import { useTheme } from '../context/ThemeContext';
import {
  ExternalWalletLogin,
  DelegatedLogin,
  GuestModeLogin
} from '../components/login';
import { DelegatedWallet } from '../wallets';


const LoginPage: FC = () => {
  const { isConnected, setWallet, publicKey } = useWalletContext();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [isLoading, setIsLoading] = useState(false);
  const [showPrivateKeyOption, setShowPrivateKeyOption] = useState(false);
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [showMobileQR, setShowMobileQR] = useState(false);
  const bifrostService = useMemo(() => new BifrostService(), []);
  
  // State and Refs for direct app login polling
  const [appLoginSessionId, setAppLoginSessionId] = useState<string | null>(null);
  const appLoginPollingRef = useRef(false);
  const appLoginTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const appLoginDelegatedWalletRef = useRef<DelegatedWallet | null>(null);

  // Add handler for session expiry
  const handleSessionExpired = useCallback(() => {
    setShowMobileQR(false);
    setAppLoginSessionId(null); // Also clear app login session
    appLoginPollingRef.current = false;
    if (appLoginTimeoutRef.current) {
      clearTimeout(appLoginTimeoutRef.current);
    }
  }, []);

  // For secret version click feature
  const [versionClicks, setVersionClicks] = useState(0);
  const [firstClickTime, setFirstClickTime] = useState<number | null>(null);


  
  useEffect(() => {
    if (isConnected && publicKey) {
      router.navigate({ 
        to: '/',
        replace: true
      });
    }
  }, [isConnected, publicKey]);
  
  const handleVersionClick = useCallback(() => {
    const currentTime = Date.now();
    
    // If this is the first click or if it's been more than 10 seconds since the first click, reset
    if (firstClickTime === null || currentTime - firstClickTime > 10000) {
      setVersionClicks(1);
      setFirstClickTime(currentTime);
    } else {
      // Increment the click count
      const newClickCount = versionClicks + 1;
      setVersionClicks(newClickCount);
      
      // If we've reached 7 clicks, show the private key option
      if (newClickCount >= 7 && !showPrivateKeyOption) {
        setShowPrivateKeyOption(true);
        // Optional: provide subtle feedback
        toast.success('Developer options unlocked', {
          icon: '🔓',
          duration: 2000
        });
      }
    }
  }, [versionClicks, firstClickTime, showPrivateKeyOption]);

  const toggleGuestMode = useCallback(() => {
    setIsGuestMode(!isGuestMode);
  }, [isGuestMode]);

  const clearAppLoginPolling = useCallback(() => {
    appLoginPollingRef.current = false;
    if (appLoginTimeoutRef.current) {
      clearTimeout(appLoginTimeoutRef.current);
    }
  }, []);

  const pollForAppLoginToken = useCallback(async () => {
    if (!appLoginPollingRef.current || !appLoginSessionId || !appLoginDelegatedWalletRef.current) {
      return;
    }
    try {
      const session = await bifrostService.getSession(); 
      if (session.token && appLoginDelegatedWalletRef.current) {
        appLoginDelegatedWalletRef.current.setJwtToken(session.token);
        bifrostService.clearSession(); 
        clearAppLoginPolling();
        setIsLoading(false);
        setWallet(appLoginDelegatedWalletRef.current);
        setAppLoginSessionId(null); 
        appLoginDelegatedWalletRef.current = null;
        return;
      }
      if (appLoginPollingRef.current) {
        appLoginTimeoutRef.current = setTimeout(pollForAppLoginToken, 4000);
      }
    } catch (error: any) {
      if (error.status === 410) { 
        clearAppLoginPolling();
        toast.error('Login session expired or invalid. Please try again.');
        handleSessionExpired(); 
      } else if (appLoginPollingRef.current) {
        appLoginTimeoutRef.current = setTimeout(pollForAppLoginToken, 4000);
      }
      console.error('Error polling for app login token:', error);
    }
  }, [appLoginSessionId, bifrostService, setWallet, handleSessionExpired, clearAppLoginPolling]);

  useEffect(() => {
    if (appLoginSessionId && !appLoginPollingRef.current && appLoginDelegatedWalletRef.current) {
      appLoginPollingRef.current = true;
      pollForAppLoginToken();
    }
    return () => {
      if (!appLoginSessionId) { 
        clearAppLoginPolling();
      }
    };
  }, [appLoginSessionId, pollForAppLoginToken, clearAppLoginPolling]);

  // Handle direct app login
  const handleAppLogin = async () => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    // For desktop: reveal QR code instead of direct app login
    if (!isMobile) {
      // Simply show the QR code - the DelegatedLogin component will handle wallet creation
      setShowMobileQR(true);
      return;
    }
    
    // For mobile: keep existing functionality
    setIsLoading(true);
    clearAppLoginPolling(); // Clear any previous polling
    setAppLoginSessionId(null);
    appLoginDelegatedWalletRef.current = null;

    try {
      const tempDelegatedWallet = new DelegatedWallet();
      appLoginDelegatedWalletRef.current = tempDelegatedWallet; // Store ref for polling
      const publicKeyString = tempDelegatedWallet.getLocalPublicKey();
      const newSessionId = await bifrostService.createSession(publicKeyString);
      
      setAppLoginSessionId(newSessionId); // This will trigger the polling useEffect

      const customAppUrl = `sourceful://login/delegate?session=${newSessionId}`;
      
      window.location.href = customAppUrl;
      setTimeout(() => {
        // Check if still on the page and polling hasn't succeeded
        if (window.document.hasFocus() && appLoginPollingRef.current) { 
          const fallbackUrl = `https://app.sourceful.energy/login/delegate?session=${newSessionId}&type=qr`;
          window.location.href = fallbackUrl;
        }
      }, 1500); // Increased timeout for app to open
      
      // setIsLoading(false) is handled by the polling logic or finally block if session creation fails upfront
    } catch (error) {
      console.error('Failed to initiate app login (session creation):', error);
      toast.error('Failed to start app login. Please try again.');
      appLoginDelegatedWalletRef.current = null; // Clear ref on error
      setIsLoading(false); // Ensure loading is stopped on initial error
    }
    // No finally block for setIsLoading(false) here, as polling continues
  };

  // Dynamically generate classes based on theme
  const bgColorClass = isDarkMode ? 'bg-gray-900' : 'bg-gray-50';
  const loginCardBgClass = isDarkMode ? 'bg-[#1D1D1D]' : 'bg-white';
  const loginCardBorderClass = isDarkMode ? 'border-[#292929]' : 'border-gray-200';
  const textPrimaryClass = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondaryClass = isDarkMode ? 'text-gray-300' : 'text-gray-800';
  const textTertiaryClass = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const dividerClass = isDarkMode ? 'bg-[#292929]' : 'bg-gray-200';

  return (
    <div className={`min-h-[100svh] ${bgColorClass} flex flex-col overflow-y-auto`}>
      <div className="px-[10px] sm:px-0">
        <div className="w-[200px] sm:w-[300px] sm:mx-auto mt-[100px]">
          <SourcfulEnergyLogo className="w-full h-auto" />
        </div>
      </div>

      {/* Deprecation Notice */}
      <div className="px-[10px] sm:px-0 mt-4 sm:mt-6">
        <div className="max-w-[600px] mx-auto">
          <div className={`bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-500 p-4 rounded-r-lg`}>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400 dark:text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className={`text-sm ${isDarkMode ? 'text-yellow-200' : 'text-yellow-800'}`}>
                  <span className="font-semibold">This dashboard is deprecated and should not be used.</span> Please use the{" "}
                  <a 
                    href="https://app.sourceful.energy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`underline font-medium ${isDarkMode ? 'hover:text-yellow-100' : 'hover:text-yellow-900'}`}
                  >
                    Sourceful App
                  </a>{" "}
                  instead.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="sm:flex-1 sm:flex sm:items-center sm:justify-center">
        <div className={`${loginCardBgClass} border ${loginCardBorderClass} rounded-[8px] py-10 px-5 sm:p-12 w-[calc(100%-20px)] sm:w-auto mx-auto sm:mx-0 mt-[calc(100svh-600px)] sm:mt-0 mb-[100px] sm:mb-0`}>
          {isGuestMode ? (
            <div className="w-full md:w-[920px] h-auto md:h-[360px]">
              <GuestModeLogin
                onBack={() => setIsGuestMode(false)}
                onSuccess={setWallet}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
              />
            </div>
          ) : (
            <div className="w-full md:w-[920px] h-auto md:h-[360px]">
              <div className="grid grid-cols-1 md:grid-cols-[1fr,auto,1fr] gap-7">
                {/* Left Side - External Wallet Login */}
                <div className="h-full flex flex-col justify-between">
                  <div>
                    <h2 className={`${textTertiaryClass} text-[24px] mb-2 sm:-mb-1 font-normal opacity-40`}>Log in to</h2>
                    <h1
                      className={`${textPrimaryClass} text-[32px] sm:text-[32px] leading-[160%] sm:leading-[140%] tracking-tight login-heading`}
                    >
                      Dashboard
                    </h1>
                    <p className={`${textTertiaryClass} text-[14px] mt-4 mb-8 md:mb-0 font-normal font-inter`}>
                      Manage all your energy resources in one place
                    </p>
                  </div>

                  <div className="flex flex-col gap-3">
                    {/* New primary button for app login */}
                    <button
                      onClick={handleAppLogin}
                      disabled={isLoading}
                      className="w-full py-4 px-6 bg-[#00CC6A] rounded-xl text-[#2B2B2B] font-semibold hover:opacity-90 transition-opacity font-inter flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {isLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#2B2B2B]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing...
                        </>
                      ) : (
                        "Log in with the Sourceful App"
                      )}
                    </button>
                    
                    {/* External wallet login now uses the modified component with variant prop */}
                    <ExternalWalletLogin variant="muted" />
                  </div>
                </div>

                {/* Vertical Divider (without text) */}
                <div className="hidden md:flex flex-col items-center justify-center relative h-full">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className={`w-px h-full ${dividerClass}`}></div>
                  </div>
                  <div className="relative px-2 py-4">
                  </div>
                </div>

                {/* Right Side - Delegated Login (Desktop only) */}
                <div className="hidden md:flex flex-col items-center justify-center">
                  <DelegatedLogin
                    onSuccess={setWallet}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                    bifrostService={bifrostService}
                    initialRevealed={showMobileQR}
                    onSessionExpired={handleSessionExpired}
                  />
                </div>

                {/* Mobile QR Code - Now directly showing the DelegatedLogin when needed */}
                <div className="md:hidden flex flex-col items-center justify-center -mt-1">
                  {showMobileQR && (
                    <DelegatedLogin
                      onSuccess={setWallet}
                      isLoading={isLoading}
                      setIsLoading={setIsLoading}
                      bifrostService={bifrostService}
                      initialRevealed={true}
                      onSessionExpired={handleSessionExpired}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer with Guest Mode link */}
      <div className="p-6 pb-[100px] sm:pb-6">
        <div className="flex justify-center space-y-6 flex-col items-center">
          {/* Guest Mode link added above the other links */}
          <div className="mb-2">
            <button 
              onClick={toggleGuestMode}
              className={`${textSecondaryClass} hover:text-[#00CC6A] transition-colors text-[14px] font-medium font-inter`}
            >
              Guest Mode
            </button>
          </div>
          <div className="flex space-x-8">
            <a href="https://onelink.to/52cdvt" className={`${textSecondaryClass} hover:text-[#00CC6A] transition-colors text-[14px] font-medium font-inter`}>Get the App</a>
            <a href="https://support.sourceful.energy/" className={`${textSecondaryClass} hover:text-[#00CC6A] transition-colors text-[14px] font-medium font-inter`}>Support</a>
            <a href="https://discord.com/invite/srcful" className={`${textSecondaryClass} hover:text-[#00CC6A] transition-colors text-[14px] font-medium font-inter`}>Community</a>
            <a href="https://store.sourceful.energy/" className={`${textSecondaryClass} hover:text-[#00CC6A] transition-colors text-[14px] font-medium font-inter`}>Store</a>
          </div>
          <div className={`${textSecondaryClass} text-[14px] font-medium font-inter`}>
            © {new Date().getFullYear()} Sourceful Energy
          </div>
          <div 
            className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-[14px] font-normal font-inter`}
            onClick={handleVersionClick}
          >
            {APP_VERSION}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 